/**
 * Fuel Analytics Sub-section
 * Advanced fuel spending and efficiency insights with predictive analytics
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Fuel, RefreshCw, Droplet, PoundSterling, Target, Zap } from 'lucide-react';
import { JigsawData, ApiStatus } from '../../types/fleetDataHub';
import LineChart from '../LineChart';
import type { TimePeriod } from '../TimePeriodSelector';
import ControlCentreKPICard from './ControlCentreKPICard';
import ControlCentrePeriodPanel, { coerceControlCentrePeriod } from './ControlCentrePeriodPanel';
import FuelDataIntakeSection from './FuelDataIntakeSection';
import FuelEfficiencySection from './FuelEfficiencySection';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';
import { CHART_PALETTE } from '../../config/colors';
import { fuelAnalyticsSubtitle } from '../../utils/ingestSourceLabels';
import { formatUkAsOf } from '../../lib/calc';
import {
  londonLastMonthRange,
  londonMonthToDateRange,
} from '../../utils/ordersDailyPresentation';

// Column definitions for fuel KPI detail modal
const FUEL_COLUMNS: ColumnDef[] = [
  { key: 'date', header: 'Date' },
  { key: 'vehicle', header: 'Vehicle' },
  { key: 'fuelType', header: 'Fuel Type' },
  { key: 'litres', header: 'Litres', align: 'right', render: (v) => v ? `${Number(v).toFixed(1)} L` : '-' },
  { key: 'cost', header: 'Cost', align: 'right', render: (v) => v ? `£${Number(v).toFixed(2)}` : '-' },
  { key: 'pricePerLitre', header: 'Price/L', align: 'right', render: (v) => v ? `£${Number(v).toFixed(3)}` : '-' },
];

/** After an upload or price save: when to read "Data current as of" again. */
const FUEL_DATA_AS_OF_RETRY_DELAYS_MS = [3_000, 15_000, 60_000] as const;

interface FuelDataAsOf {
  data_current_as_of: string | null;
  transactions_uploaded_at: string | null;
  prices_updated_at: string | null;
}

interface FuelAnalyticsProps {
  data: JigsawData | null;
  apiStatus: ApiStatus | undefined;
  onFetchData: () => void;
  isLoading: boolean;
  highlightKPI?: string | null;
  /** Clear parent highlight after alert deep-link is consumed (avoids modal reopen loops) */
  onHighlightConsumed?: () => void;
  /** When set (e.g. from Fleet Alerts), sync the section time period to this value */
  initialPeriod?: TimePeriod;
  /** Demo/mock mode — skip truth-layer period fetches */
  useMockData?: boolean;
  /** Org-configured fuel source display name, e.g. "Jigsaw" */
  fuelSourceLabel?: string;
  /** Org-configured fuel source ids for subtitle helpers */
  fuelSourceIds?: string[];
}

export default function FuelAnalytics({
  data,
  onFetchData,
  isLoading,
  highlightKPI,
  onHighlightConsumed,
  initialPeriod,
  useMockData = false,
  fuelSourceLabel,
  fuelSourceIds = [],
}: FuelAnalyticsProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    () => coerceControlCentrePeriod(initialPeriod, 'last-week'),
  );
  const [periodData, setPeriodData] = useState<JigsawData | null>(null);
  const [periodDataRange, setPeriodDataRange] = useState<{ from: string; to: string } | null>(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);
  const periodDataRangeRef = useRef<{ from: string; to: string } | null>(null);

  // KPI detail modal state
  const [kpiModal, setKpiModal] = useState<{ open: boolean; title: string; subtitle?: string }>({ open: false, title: '' });
  const kpiGridRef = useRef<HTMLDivElement>(null);

  // Sync time period when navigated from Fleet Alerts with a specific period
  useEffect(() => {
    if (initialPeriod != null) {
      setSelectedPeriod(coerceControlCentrePeriod(initialPeriod, 'last-week'));
    }
  }, [initialPeriod]);

  // Calculate date range based on selected period
  const getDateRange = useMemo(() => {
    // Use UTC dates for consistency with backend/BigQuery data
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD in UTC
    
    let startDate: Date;
    let endDate: Date;
    
    switch (selectedPeriod) {
      case 'today':
        startDate = new Date(todayStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setUTCDate(yesterday.getUTCDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        startDate = new Date(yesterdayStr + 'T00:00:00.000Z');
        endDate = new Date(yesterdayStr + 'T23:59:59.999Z');
        break;
      }
      case 'last-week': {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setUTCDate(sevenDaysAgo.getUTCDate() - 7);
        const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
        startDate = new Date(sevenDaysAgoStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'month-to-date': {
        // London calendar month — same cut-off as Orders / Fleet Alerts.
        const { start, end } = londonMonthToDateRange();
        startDate = new Date(start + 'T00:00:00.000Z');
        endDate = new Date(end + 'T23:59:59.999Z');
        break;
      }
      case 'last-month': {
        const { start, end } = londonLastMonthRange();
        startDate = new Date(start + 'T00:00:00.000Z');
        endDate = new Date(end + 'T23:59:59.999Z');
        break;
      }
      case 'last_30_days': {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - 30);
        const startStr = d.toISOString().split('T')[0];
        startDate = new Date(startStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'last_180_days': {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - 180);
        const startStr = d.toISOString().split('T')[0];
        startDate = new Date(startStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'all-time':
        startDate = new Date('2000-01-01T00:00:00.000Z');
        endDate = new Date('2099-12-31T23:59:59.999Z');
        break;
      case 'custom':
      default: {
        // Chip bar has no custom picker — treat unknown/custom like Last 7 Days.
        const defaultStart = new Date(now);
        defaultStart.setUTCDate(defaultStart.getUTCDate() - 7);
        const defaultStartStr = defaultStart.toISOString().split('T')[0];
        startDate = new Date(defaultStartStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
      }
    }
    
    return { startDate, endDate };
  }, [selectedPeriod]);

  const fetchPeriodSummary = useCallback(async () => {
    if (useMockData) {
      setPeriodData(null);
      setPeriodDataRange(null);
      setPeriodError(null);
      return;
    }

    const { startDate, endDate } = getDateRange;
    const from = startDate.toISOString();
    const to = endDate.toISOString();
    setPeriodLoading(true);
    setPeriodError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.fuelSummary({ from, to }));
      if (!res.ok) {
        throw new Error(`Fuel summary failed (${res.status})`);
      }
      const json = await res.json();
      setPeriodData(json as JigsawData);
      setPeriodDataRange({ from, to });
      periodDataRangeRef.current = { from, to };
    } catch (err) {
      console.error('[FuelAnalytics] Period summary fetch failed:', err);
      setPeriodError(err instanceof Error ? err.message : 'Failed to load fuel data');
      const matched =
        periodDataRangeRef.current?.from === from && periodDataRangeRef.current?.to === to;
      if (!matched) {
        setPeriodData(null);
        setPeriodDataRange(null);
        periodDataRangeRef.current = null;
      }
    } finally {
      setPeriodLoading(false);
    }
  }, [useMockData, getDateRange]);

  useEffect(() => {
    void fetchPeriodSummary();
  }, [fetchPeriodSummary]);

  // "Data current as of": the later of the last transaction upload and the last
  // price update (server, from stored received_at), with both shown beside it.
  const [dataAsOf, setDataAsOf] = useState<FuelDataAsOf | null>(null);
  const dataAsOfRef = useRef<FuelDataAsOf | null>(null);
  const dataAsOfGenRef = useRef(0);
  const dataAsOfTimerRef = useRef<number | null>(null);
  /** Reads the times and shows them; returns what was read (null on failure or when superseded). */
  const fetchDataAsOf = useCallback(async (): Promise<FuelDataAsOf | null> => {
    const gen = ++dataAsOfGenRef.current;
    const show = (value: FuelDataAsOf | null) => {
      if (gen !== dataAsOfGenRef.current) return null;
      dataAsOfRef.current = value;
      setDataAsOf(value);
      return value;
    };
    if (useMockData) return show(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.fuelDataCurrentAsOf());
      if (!res.ok) throw new Error(`Fuel data_current_as_of failed (${res.status})`);
      return show((await res.json()) as FuelDataAsOf);
    } catch (err) {
      // Left out rather than shown wrong.
      console.error('[FuelAnalytics] data_current_as_of fetch failed:', err);
      return show(null);
    }
  }, [useMockData]);

  useEffect(() => {
    void fetchDataAsOf();
  }, [fetchDataAsOf]);

  // Uploads and price saves are queued, and the truth writer commits them on its
  // next flush, often well after 3 s. Read again with back-off until a time moves.
  const refreshDataAsOfSoon = useCallback(() => {
    if (dataAsOfTimerRef.current !== null) window.clearTimeout(dataAsOfTimerRef.current);
    const before = dataAsOfRef.current;
    const moved = (now: FuelDataAsOf | null) =>
      now !== null
      && (now.transactions_uploaded_at !== before?.transactions_uploaded_at
        || now.prices_updated_at !== before?.prices_updated_at);
    const attempt = (delays: readonly number[]) => {
      const [delay, ...rest] = delays;
      if (delay === undefined) return;
      dataAsOfTimerRef.current = window.setTimeout(() => {
        dataAsOfTimerRef.current = null;
        void fetchDataAsOf().then((now) => {
          if (!moved(now)) attempt(rest);
        });
      }, delay);
    };
    attempt(FUEL_DATA_AS_OF_RETRY_DELAYS_MS);
  }, [fetchDataAsOf]);

  useEffect(() => () => {
    if (dataAsOfTimerRef.current !== null) window.clearTimeout(dataAsOfTimerRef.current);
  }, []);

  const handleRefresh = useCallback(() => {
    if (useMockData) {
      onFetchData();
      return;
    }
    void fetchPeriodSummary();
  }, [useMockData, onFetchData, fetchPeriodSummary]);

  const { startDate: rangeStart, endDate: rangeEnd } = getDateRange;
  const selectedFrom = rangeStart.toISOString();
  const selectedTo = rangeEnd.toISOString();
  const periodDataMatchesSelection =
    !!periodData
    && periodDataRange?.from === selectedFrom
    && periodDataRange?.to === selectedTo;

  // Live mode: use period-scoped fuel/summary. Never fall back to the hub's
  // default 7-day preload — that made 180d / all-time charts look empty.
  const sourceData = useMockData ? data : (periodDataMatchesSelection ? periodData : null);

  // Filter fuel data based on selected time period (mock / already-scoped live).
  const filteredData = useMemo(() => {
    if (!sourceData) return null;
    // Live API response is already scoped to the selected from/to.
    if (!useMockData) return sourceData;
    if (selectedPeriod === 'all-time') return sourceData;

    const { startDate, endDate } = getDateRange;

    // Create a filtered copy of the data
    const filtered = { ...sourceData };

    // Filter daily series by date range (UTC day keys)
    if (filtered.daily_transactions) {
      const filteredDaily: Record<string, number> = {};
      const filteredSpend: Record<string, number> = {};
      let filteredTransactionCount = 0;
      let filteredTotalLitres = 0;
      let filteredTotalCost = 0;
      const spendByDay = filtered.daily_spend ?? {};

      Object.entries(filtered.daily_transactions).forEach(([date, count]) => {
        const dateStr = String(date).split('T')[0];
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
        const transactionDate = new Date(`${dateStr}T00:00:00.000Z`);

        if (transactionDate >= startDate && transactionDate <= endDate) {
          filteredDaily[date] = count;
          filteredTransactionCount += count;

          const dayRatio = count / (filtered.transaction_count || 1);
          filteredTotalLitres += (filtered.total_fuel_litres || 0) * dayRatio;

          const hasDaySpend = date in spendByDay;
          if (hasDaySpend) {
            const daySpend = Number(spendByDay[date] ?? 0);
            filteredSpend[date] = daySpend;
            filteredTotalCost += daySpend;
          } else {
            filteredTotalCost += (filtered.total_fuel_cost || 0) * dayRatio;
          }
        }
      });

      filtered.daily_transactions = filteredDaily;
      if (Object.keys(filteredSpend).length > 0) {
        filtered.daily_spend = filteredSpend;
      }
      filtered.transaction_count = filteredTransactionCount;
      filtered.total_fuel_litres = Math.round(filteredTotalLitres * 100) / 100;
      filtered.total_fuel_cost = Math.round(filteredTotalCost * 100) / 100;
    }

    const transactionRatio = filtered.transaction_count > 0 && (sourceData?.transaction_count || 0) > 0
      ? filtered.transaction_count / (sourceData.transaction_count || 1)
      : 1;

    if (filtered.spending_by_vehicle && transactionRatio < 1) {
      filtered.spending_by_vehicle = filtered.spending_by_vehicle.map(item => ({
        ...item,
        amount: Math.round(item.amount * transactionRatio * 100) / 100
      }));
    }

    if (filtered.spending_by_driver && transactionRatio < 1) {
      filtered.spending_by_driver = filtered.spending_by_driver.map(item => ({
        ...item,
        amount: Math.round(item.amount * transactionRatio * 100) / 100
      }));
    }

    if (filtered.top_spending_vehicles && transactionRatio < 1) {
      filtered.top_spending_vehicles = filtered.top_spending_vehicles.map(item => ({
        ...item,
        amount: Math.round(item.amount * transactionRatio * 100) / 100
      }));
    }

    return filtered;
  }, [sourceData, useMockData, selectedPeriod, getDateRange]);

  const displayDataForHooks = filteredData || sourceData;

  // Build synthetic fuel transaction rows from daily_transactions + spending_by_vehicle
  const fuelRows = useMemo(() => {
    if (!displayDataForHooks) return [];
    const rows: any[] = [];
    const vehicles = displayDataForHooks.spending_by_vehicle || displayDataForHooks.top_spending_vehicles || [];
    const fuelTypes = displayDataForHooks.fuel_types ? Object.keys(displayDataForHooks.fuel_types) : ['Diesel'];
    const dailyTx = displayDataForHooks.daily_transactions || {};
    const dates = Object.keys(dailyTx).sort();
    
    // Spread vehicle spending across dates proportionally
    dates.forEach(date => {
      const txCount = dailyTx[date] || 0;
      if (txCount === 0) return;
      const totalLitres = displayDataForHooks.total_fuel_litres > 0 
        ? (displayDataForHooks.total_fuel_litres / Math.max(1, Object.values(dailyTx).reduce((s, v) => s + v, 0))) * txCount
        : 0;
      const totalCost = displayDataForHooks.total_fuel_cost > 0
        ? (displayDataForHooks.total_fuel_cost / Math.max(1, Object.values(dailyTx).reduce((s, v) => s + v, 0))) * txCount
        : 0;
      const perTxLitres = totalLitres / txCount;
      const perTxCost = totalCost / txCount;

      for (let i = 0; i < Math.min(txCount, 5); i++) {
        const vehicle = vehicles[i % Math.max(1, vehicles.length)];
        rows.push({
          date,
          vehicle: vehicle?.vehicle || `Vehicle-${i + 1}`,
          fuelType: fuelTypes[i % fuelTypes.length],
          litres: perTxLitres,
          cost: perTxCost,
          pricePerLitre: perTxLitres > 0 ? perTxCost / perTxLitres : 0,
        });
      }
    });
    return rows;
  }, [displayDataForHooks]);

  // Auto-open KPI modal once when navigated from Control Centre alerts
  useEffect(() => {
    if (!highlightKPI || !displayDataForHooks) return;
    const timer = setTimeout(() => {
      kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (highlightKPI === 'avg-price') {
        const avg =
          (displayDataForHooks.total_fuel_litres || 0) > 0
            ? (displayDataForHooks.total_fuel_cost || 0) / (displayDataForHooks.total_fuel_litres || 1)
            : 0;
        setKpiModal({
          open: true,
          title: 'Price per Litre',
          subtitle: `Average: £${avg.toFixed(2)} per litre`,
        });
      } else {
        // fuel-purchased (default)
        setKpiModal({
          open: true,
          title: 'Fuel Purchased',
          subtitle: `${displayDataForHooks.total_fuel_litres?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0} litres total`,
        });
      }
      onHighlightConsumed?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightKPI, displayDataForHooks, onHighlightConsumed]);

  const showLoading =
    isLoading
    || periodLoading
    || (!useMockData && !periodDataMatchesSelection && !periodError);

  const headerAndIntake = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Fuel className="w-7 h-7 text-chart-2" />
            Fuel Spending Intelligence
          </h3>
          <p className="text-muted-foreground mt-1">{fuelAnalyticsSubtitle(fuelSourceIds)}</p>
          {!useMockData && dataAsOf?.data_current_as_of && (
            <p className="text-muted-foreground text-xs mt-1">
              Data current as of {formatUkAsOf(dataAsOf.data_current_as_of, new Date(), '')}
              <span className="text-muted-foreground/70">
                {' · '}Transactions updated{' '}
                {dataAsOf.transactions_uploaded_at
                  ? formatUkAsOf(dataAsOf.transactions_uploaded_at, new Date(), '')
                  : 'never'}
                {' · '}Price updated{' '}
                {dataAsOf.prices_updated_at ? formatUkAsOf(dataAsOf.prices_updated_at, new Date(), '') : 'never'}
              </span>
            </p>
          )}
        </div>
        <ControlCentreSourceBadge label={fuelSourceLabel} />
      </div>

      <FuelDataIntakeSection
        useMockData={useMockData}
        onTransactionsUploaded={() => {
          void fetchPeriodSummary();
          refreshDataAsOfSoon();
        }}
        onPriceSaved={refreshDataAsOfSoon}
      />
    </div>
  );

  // If no data, show fetch prompt (intake still available above)
  if (!sourceData && !showLoading) {
    return (
      <div className="space-y-6">
        {headerAndIntake}
        <div className="text-center py-12">
          <Fuel className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Fuel Spending Overview</h3>
          <p className="text-muted-foreground mb-6">
            Track your fuel costs, find savings opportunities, and monitor spending patterns
            {fuelSourceLabel ? ` (${fuelSourceLabel})` : ''}
          </p>
          {periodError && <p className="text-destructive mb-4 text-sm">{periodError}</p>}
          <button
            onClick={handleRefresh}
            disabled={showLoading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-chart-2 hover:bg-chart-2 disabled:bg-chart-2/50 text-white rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${showLoading ? 'animate-spin' : ''}`} />
            {showLoading
              ? (fuelSourceLabel ? `Loading (${fuelSourceLabel})...` : 'Loading...')
              : 'Load Fuel Data'}
          </button>
        </div>
      </div>
    );
  }

  if (showLoading && !sourceData) {
    return (
      <div className="space-y-6">
        {headerAndIntake}
        <div className="text-center py-12">
          <RefreshCw className="w-10 h-10 text-chart-2 mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">
            {fuelSourceLabel
              ? `Loading fuel data (${fuelSourceLabel}) for the selected period…`
              : 'Loading fuel data for the selected period…'}
          </p>
        </div>
      </div>
    );
  }

  // Use filtered data if available, otherwise use original data
  // After the early return check above, data is guaranteed to be non-null
  const displayData = filteredData || sourceData!;

  // Calculate insights from filtered/display data
  const avgCostPerLitre = displayData.total_fuel_litres > 0
    ? displayData.total_fuel_cost / displayData.total_fuel_litres
    : 0;

  // Daily Fill-ups — up to ~1 calendar month of bars for the selected period
  const dailyFillupDays = (() => {
    const entries = Object.entries(displayData.daily_transactions || {})
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-31);
    return entries.map(([date, count], index) => {
      const d = new Date(`${date}T12:00:00`);
      const prev = index > 0 ? entries[index - 1][0] : null;
      const monthChanged = !prev || prev.slice(0, 7) !== date.slice(0, 7);
      // Day-only ticks are ambiguous across month boundaries — show "1 Sep" style then.
      const label =
        index === 0 || monthChanged
          ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : d.toLocaleDateString('en-GB', { day: '2-digit' });
      return {
        day: date,
        label,
        count: Number(count) || 0,
      };
    });
  })();
  const maxDailyFillups = Math.max(1, ...dailyFillupDays.map((d) => d.count));
  // Nice round y-axis ceiling so ticks are even (e.g. 13 → 16 or 15)
  const fillupYAxisMax = (() => {
    if (maxDailyFillups <= 5) return 5;
    const rough = maxDailyFillups * 1.05;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const normalized = rough / magnitude;
    const nice = normalized <= 1.5 ? 1.5 : normalized <= 2 ? 2 : normalized <= 3 ? 3 : normalized <= 5 ? 5 : 10;
    return Math.ceil(nice * magnitude);
  })();
  const fillupYTicks = [0, 0.25, 0.5, 0.75, 1].map((pct) => Math.round(fillupYAxisMax * pct));

  // Daily spend (£) for Spending Trend — fill-up counts stay on the bar chart / overview health
  const spendByDay = displayData.daily_spend ?? {};
  const trendUsesSpend = Object.keys(spendByDay).length > 0;
  const trendData = Object.entries(
    trendUsesSpend ? spendByDay : (displayData.daily_transactions || {}),
  )
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-30)
    .map(([date, value], index, arr) => {
      // Parse at local noon so the label never slips to the previous day west
      // of UTC; show "1 Sep" at the first tick and each month change since a
      // 30-day window routinely spans a boundary.
      const d = new Date(`${date}T12:00:00`);
      const prev = index > 0 ? arr[index - 1][0] : null;
      const monthChanged = !prev || prev.slice(0, 7) !== date.slice(0, 7);
      const label =
        index === 0 || monthChanged
          ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : d.toLocaleDateString('en-GB', { day: '2-digit' });
      return { label, value: Number(value) || 0 };
    });

  return (
    <div className="space-y-6">
      {headerAndIntake}

      <div className="flex flex-wrap items-center gap-3">
        <ControlCentrePeriodPanel
          value={selectedPeriod}
          onChange={setSelectedPeriod}
          accent="orange"
          ariaLabel="Fuel time period"
        />
        <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
          {selectedFrom.slice(0, 10) === selectedTo.slice(0, 10)
            ? `(${selectedFrom.slice(0, 10)})`
            : `(${selectedFrom.slice(0, 10)} → ${selectedTo.slice(0, 10)})`}
        </span>
      </div>

      {/* Key Metrics */}
      <div ref={kpiGridRef} className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ControlCentreKPICard
          id="kpi-total-spent"
          title="Total Spent"
          value={`£${displayData.total_fuel_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0}`}
          subtitle="On fuel purchases"
          icon={PoundSterling}
          variant="primary"
          colorScheme="amber"
          valueHoverScale={1.15}
          onClick={() => setKpiModal({ open: true, title: 'Total Fuel Spend', subtitle: `£${displayData.total_fuel_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0} across all transactions` })}
        />

        <ControlCentreKPICard
          id="kpi-fuel-purchased"
          title="Fuel Purchased"
          value={displayData.total_fuel_litres?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0}
          subtitle="Litres total"
          icon={Droplet}
          variant="primary"
          colorScheme="blue"
          valueHoverScale={1.15}
          onClick={() => setKpiModal({ open: true, title: 'Fuel Purchased', subtitle: `${displayData.total_fuel_litres?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0} litres total` })}
        />

        <ControlCentreKPICard
          id="kpi-avg-price"
          title="Avg Price"
          value={`£${avgCostPerLitre.toFixed(2)}`}
          subtitle="Per litre"
          icon={Target}
          variant="primary"
          colorScheme="green"
          valueHoverScale={1.15}
          onClick={() => setKpiModal({ open: true, title: 'Price per Litre', subtitle: `Average: £${avgCostPerLitre.toFixed(2)} per litre` })}
        />

        <ControlCentreKPICard
          id="kpi-fillups"
          title="Fill-ups"
          value={displayData.transaction_count?.toLocaleString() || 0}
          subtitle="Total transactions"
          icon={Zap}
          variant="primary"
          colorScheme="cyan"
          valueHoverScale={1.15}
          onClick={() => setKpiModal({ open: true, title: 'Fill-up Transactions', subtitle: `${displayData.transaction_count?.toLocaleString() || 0} total transactions` })}
        />
      </div>

      {/* Full-width charts: Spending Trend, then Daily Fill-ups */}
      <div className="space-y-6">
        {trendData.length > 0 && (
          <div className="bg-card/50 rounded-lg border border-border min-w-0 overflow-hidden">
            <div className="px-4 pt-3 pb-3">
              <h4 className="text-lg font-semibold text-white leading-none">Spending Trend (£)</h4>
            </div>
            <LineChart
              data={trendData}
              title=""
              heightClass="h-64"
              chartHeight={240}
              color={CHART_PALETTE[1]}
              formatValueAsInteger
              yAxisLabel=""
              xAxisLabel="Date"
              gradientFillSeriesName="Spend"
              dense
            />
          </div>
        )}

        {dailyFillupDays.length > 0 && (
          <div className="bg-card/50 rounded-lg border border-border min-w-0 overflow-hidden">
            <div className="px-4 pt-3 pb-3">
              <h4 className="text-lg font-semibold text-white leading-none">Daily Fill-ups</h4>
            </div>
            <div className="flex gap-2 px-3 pb-3">
              {/* Y ticks aligned to the bar plot box only (below value-label gutter) */}
              <div className="relative w-8 shrink-0" style={{ height: 128, marginTop: 20 }}>
                {fillupYTicks.map((tick) => {
                  const pct = fillupYAxisMax > 0 ? tick / fillupYAxisMax : 0;
                  return (
                    <span
                      key={tick}
                      className="absolute right-0 text-xs tabular-nums text-muted-foreground leading-none -translate-y-1/2"
                      style={{ bottom: `${pct * 100}%` }}
                    >
                      {tick}
                    </span>
                  );
                })}
              </div>
              <div className="flex-1 min-w-0">
                <div className="relative" style={{ height: 148 }}>
                  {/* Value-label gutter (20px) + plot (128px) */}
                  <div className="absolute inset-x-0 bottom-0 top-5">
                    {fillupYTicks.map((tick) => {
                      const pct = fillupYAxisMax > 0 ? tick / fillupYAxisMax : 0;
                      return (
                        <div
                          key={`grid-${tick}`}
                          className="absolute inset-x-0 border-t border-dashed border-border/70 pointer-events-none"
                          style={{ bottom: `${pct * 100}%` }}
                        />
                      );
                    })}
                    <div className="absolute inset-0 flex items-end gap-2">
                      {dailyFillupDays.map((d) => (
                        <div
                          key={d.day}
                          className="flex-1 h-full flex items-end min-w-0"
                          title={`${d.label}: ${d.count} fill-up${d.count === 1 ? '' : 's'}`}
                        >
                          <div
                            className="w-full rounded-t relative bg-gradient-to-t from-chart-2/40 to-chart-2"
                            style={{
                              height: `${Math.max(d.count > 0 ? 3 : 0, (d.count / fillupYAxisMax) * 100)}%`,
                            }}
                          >
                            <span className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 text-sm font-semibold tabular-nums whitespace-nowrap text-white">
                              {d.count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-1.5">
                  {dailyFillupDays.map((d) => (
                    <span
                      key={`${d.day}-label`}
                      className="flex-1 text-center text-xs font-medium tracking-wide text-muted-foreground truncate"
                    >
                      {d.label}
                    </span>
                  ))}
                </div>
                <div className="pt-1 text-center text-xs font-semibold tracking-wide text-muted-foreground">
                  Date
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Samsara fuel & energy efficiency, same period as the cards above */}
      {!useMockData && (
        <FuelEfficiencySection from={getDateRange.startDate.toISOString()} to={getDateRange.endDate.toISOString()} />
      )}

      {/* KPI Detail Modal */}
      <KPIDataModal
        isOpen={kpiModal.open}
        onClose={() => {
          setKpiModal({ open: false, title: '' });
          onHighlightConsumed?.();
        }}
        title={kpiModal.title}
        subtitle={kpiModal.subtitle}
        icon={Fuel}
        data={fuelRows}
        columns={FUEL_COLUMNS}
        searchKeys={['date', 'vehicle', 'fuelType']}
        exportFilename={`fuel_transactions_${new Date().toISOString().split('T')[0]}.csv`}
      />
    </div>
  );
}
